import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Pagination,
  Tooltip,
} from "@mui/material";
import { Add, Password, PersonOff, Person, RestartAlt } from "@mui/icons-material";

import { Breadcrumbs, PageHeader, ProgressOverlay } from "@framework/mui-page-layout";
import { IUser, IUserItem } from "@framework/types";
import { useCollection } from "@framework/react-hooks";
import { useUser } from "@framework/provider-user";

import { ResetUserSettingsDialog } from "../../components/common/reset-user-settings-dialog";

import { UserCreateDialog, UserChangePasswordDialog, UserChangeStatusDialog, usePasswordPolicy } from "./edit";

export const User: FC = () => {
  const { formatMessage } = useIntl();
  const policy = usePasswordPolicy();
  const currentUser = useUser<IUser>();
  const [resetSettingsUser, setResetSettingsUser] = useState<IUserItem | null>(null);
  const {
    rows,
    count,
    search,
    selected,
    isLoading,
    isCreateUserDialogOpen,
    isChangePasswordDialogOpen,
    isChangeStatusDialogOpen,
    openUserCreateDialog,
    handleCreateUserCancel,
    handleCreateUserConfirm,
    openChangePasswordDialog,
    handleChangePasswordCancel,
    handleChangePasswordConfirm,
    openChangeStatusDialog,
    handleChangeStatusCancel,
    handleChangeStatusConfirm,
    handleChangePage,
  } = useCollection<IUserItem>({
    baseUrl: "/users",
    empty: {
      name: "",
      surName: "",
      email: "",
      login: "",
    },
  });

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "users"]} />
      <PageHeader message="pages.users.title">
        <Button startIcon={<Add />} onClick={openUserCreateDialog} data-testid="CreateButton">
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>
      <ProgressOverlay isLoading={isLoading}>
        <List>
          {rows.map((user, i) => (
            <ListItem
              key={i}
              sx={{
                backgroundColor: user.isActive ? "inherit" : "rgba(255, 0, 0, 0.1)",
                pr: 18,
              }}
            >
              <ListItemText>
                {user.name +
                  " " +
                  user.surName +
                  " [" +
                  formatMessage({ id: `enums.userGroup.${user.userGroup}` }) +
                  "]"}
              </ListItemText>
              <ListItemSecondaryAction>
                <Tooltip followCursor title={formatMessage({ id: "form.buttons.resetSettings" })}>
                  <span>
                    <IconButton
                      aria-label={formatMessage({ id: "form.buttons.resetSettings" })}
                      disabled={user.id === currentUser.profile.id && currentUser.isSavingSettings}
                      onClick={() => {
                        if (user.id === currentUser.profile.id && currentUser.isSavingSettings) return;
                        setResetSettingsUser(user);
                      }}
                    >
                      <RestartAlt />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip followCursor title={formatMessage({ id: "form.buttons.setNewPassword" })}>
                  <IconButton onClick={openChangePasswordDialog(user)}>
                    <Password />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  followCursor
                  title={formatMessage({ id: user.isActive ? "form.buttons.deactivate" : "form.buttons.activate" })}
                >
                  <IconButton onClick={openChangeStatusDialog(user)}>
                    {user.isActive ? <PersonOff /> : <Person />}
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </ProgressOverlay>

      <Pagination
        sx={{ mt: 2 }}
        shape="rounded"
        page={search.skip / search.take + 1}
        count={Math.ceil(count / search.take)}
        onChange={handleChangePage}
      />

      <UserCreateDialog
        onCancel={handleCreateUserCancel}
        onConfirm={handleCreateUserConfirm}
        open={isCreateUserDialogOpen}
        initialValues={selected}
        policy={policy}
      />

      <UserChangePasswordDialog
        onCancel={handleChangePasswordCancel}
        onConfirm={handleChangePasswordConfirm}
        open={isChangePasswordDialogOpen}
        initialValues={{ ...selected, password: "" }}
        policy={policy}
      />

      <UserChangeStatusDialog
        onCancel={handleChangeStatusCancel}
        onConfirm={handleChangeStatusConfirm}
        open={isChangeStatusDialogOpen}
        initialValues={selected}
        getTitle={(user: IUserItem) => user.name + " " + user.surName}
      />
      {resetSettingsUser && (
        <ResetUserSettingsDialog open target={resetSettingsUser} onClose={() => setResetSettingsUser(null)} />
      )}
    </Grid>
  );
};
